import { z } from 'zod';
import * as dt from '../data.types.js';
import { allUserRoleSchema } from '../data.types.js';
import { BaseResponseSchema, containerSlotSchema, containerTypeSchema, dateSchema, emailSchema, firstNameSchema, integerIdSchema, lastNameSchema, passwordSchema, urlSchema, uuidSchema, waferAuditStatusSchema, waferFingerprintSchema, waferMaterialTypeSchema, waferPatternTypeSchema, waferSizeSchema, waferStatusSchema, waferThicknessTypeSchema, } from './primitives.js';
import { FileMetadataSchema } from './files.schemas.js';
import { UserSummarySchema } from './tenant-directory.schemas.js';
import { WaferResponseSchema, EntityFilesDeleteResponseDataSchema } from './tenant-entity-responses.schemas.js';
import { WaferContainerStaySchema } from './tenant-location.schemas.js';
import { waferHistoryEventSchema, healthStatusSchema, healthCheckStatusSchema, readinessStatusSchema, livenessStatusSchema, imageQualitySchema, mlComponentStatusSchema, } from './efem-core.schemas.js';
import { EfemGatewaySerialParamSchema } from './params.js';
export const ContainerAuditRequestSchema = z.object({
    containerId: integerIdSchema,
    slotNumber: containerSlotSchema,
    expectedFingerprint: waferFingerprintSchema.optional(),
    matchedFingerprint: waferFingerprintSchema.optional(),
    expectedWaferId: integerIdSchema.optional(),
    matchedWaferId: integerIdSchema.optional(),
    fingerprintImage: FileMetadataSchema.optional(),
    state: z.nativeEnum(dt.WAFER_AUDIT_STATUS),
    auditedBy: integerIdSchema.optional(),
});
export const ContainerAuditResponseSchema = z.object({
    id: integerIdSchema,
    auditRunId: integerIdSchema.nullable().optional(),
    containerId: integerIdSchema,
    containerName: z.string().min(1),
    slotNumber: containerSlotSchema,
    auditDate: dateSchema,
    expectedFingerprint: waferFingerprintSchema.optional().nullable(),
    expectedWaferIdentifier: z.string().optional(), //friendly name to identify the wafer. ex: ${wafer.layerName} (${wafer.type}, ${wafer.size})`
    matchedFingerprint: waferFingerprintSchema.optional().nullable(),
    matchedWaferIdentifier: z.string().optional(), //friendly name to identify the wafer. ex: ${wafer.layerName} (${wafer.type}, ${wafer.size})`
    fingerprintImage: FileMetadataSchema.optional().nullable(),
    state: z.nativeEnum(dt.WAFER_AUDIT_STATUS),
    auditedByUserId: integerIdSchema.optional().nullable(),
    auditedByUserName: z.string().optional(),
    discrepancyDetails: z.string().optional()
});
export const LoadContainersResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    loadedPorts: z.array(z.number().int().positive())
});
export const UnloadContainersResponseSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    unloadedPorts: z.array(z.number().int().positive())
});
// Audit Schemas
export const AuditRecordSchema = z.object({
    id: uuidSchema,
    containerId: integerIdSchema,
    containerName: z.string().optional(),
    slotNumber: z.number().int().positive(),
    auditDate: z.string(),
    expectedFingerprint: waferFingerprintSchema.optional(),
    expectedWaferName: z.string().optional(),
    matchedWaferId: waferFingerprintSchema.optional(),
    matchedWaferName: z.string().optional(),
    waferIdImageUrl: urlSchema,
    state: waferAuditStatusSchema,
    auditedBy: z.number().int().positive().optional(),
    auditorName: z.string().optional()
});
export const AuditHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        audits: z.array(ContainerAuditResponseSchema),
        total: z.number().int(),
        page: z.number().int(),
        limit: z.number().int(),
        totalPages: z.number().int()
    })
});
export const AuditStatsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        totalAudits: z.number().int(),
        okCount: z.number().int(),
        ghostWaferCount: z.number().int(),
        missingWaferCount: z.number().int(),
        mismatchCount: z.number().int(),
        notAuditedCount: z.number().int(),
        recentAudits: z.array(ContainerAuditResponseSchema)
    })
});
export const GetAuditByIdResponseSchema = BaseResponseSchema.extend({
    data: ContainerAuditResponseSchema
});
export const CreateAuditResponseSchema = BaseResponseSchema.extend({
    data: ContainerAuditResponseSchema
});
export const GetAuditHistoryRequestSchema = z.object({
    containerId: integerIdSchema.optional(),
    state: z.nativeEnum(dt.WAFER_AUDIT_STATUS).optional(),
    auditedBy: integerIdSchema.optional(),
    dateFrom: dateSchema.optional(),
    dateTo: dateSchema.optional(),
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const AuditHistoryFilterSchema = z.object({
    containerId: integerIdSchema.optional(),
    state: z.nativeEnum(dt.WAFER_AUDIT_STATUS).optional(),
    auditedBy: integerIdSchema.optional(),
    dateFrom: dateSchema.optional(),
    dateTo: dateSchema.optional(),
    page: z.number().int(),
    limit: z.number().int(),
});
export const AuditIdParamSchema = z.object({
    auditId: integerIdSchema
});
// Event Details Schema
export const EventDetailsSchema = z.object({
    locationName: z.string().optional().nullable(),
    locationType: z.string().optional(),
    locationDescription: z.string().optional().nullable(),
    beaconId: z.string().optional(),
    status: z.nativeEnum(dt.CONTAINER_STATUS).optional(),
    beaconVersion: z.string().optional(),
    beaconStatus: z.nativeEnum(dt.BACON_STATUS).optional(),
    serviceProvider: z.string().optional().nullable(),
    coordinates: z.object({
        latitude: z.number().optional(),
        longitude: z.number().optional(),
    }).optional(),
    transferType: z.string().optional(),
    fromContainerName: z.string().optional(),
    toContainerName: z.string().optional(),
    fromSlot: containerSlotSchema.optional(),
    toSlot: containerSlotSchema.optional(),
    waferIdentifierStr: z.string().optional(),
    notes: z.string().optional(),
    type: z.string().optional(),
    size: z.string().optional(),
    customer: z.string().optional(),
    ownerFirstName: firstNameSchema.optional(),
    ownerLastName: lastNameSchema.optional(),
    receivedDate: z.string().optional(),
});
// History Schemas
export const ContainerHistoryEventSchema = z.object({
    id: z.string().min(1),
    eventType: z.nativeEnum(dt.CONTAINER_HISTORY_EVENT),
    timestamp: dateSchema,
    description: z.string(),
    details: EventDetailsSchema,
    performedBy: UserSummarySchema.optional(),
    relatedWafer: z.object({
        id: integerIdSchema,
        waferIdStr: z.string().optional(),
        layerName: z.string()
    }).optional(),
    locationInfo: z.object({
        previousLocation: z.string().optional(),
        newLocation: z.string().optional(),
        beacon: z.string().optional()
    }).optional()
});
export const ContainerHistoryRequestSchema = z.object({
    startDate: dateSchema.optional(),
    endDate: dateSchema.optional(),
    eventTypes: z.array(z.nativeEnum(dt.CONTAINER_HISTORY_EVENT)).optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    sortBy: z.enum(['timestamp', 'eventType']).optional(),
    sortOrder: z.enum(['ASC', 'DESC']).optional()
});
export const ContainerHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        containerId: integerIdSchema,
        containerName: z.string(),
        events: z.array(ContainerHistoryEventSchema),
        totalCount: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
        hasMore: z.boolean()
    })
});
export const ContainerSummaryStatsSchema = BaseResponseSchema.extend({
    data: z.object({
        totalEvents: z.number().int(),
        waferTransfers: z.number().int(),
        statusChanges: z.number().int(),
        locationChanges: z.number().int(),
        beaconChanges: z.number().int(),
        currentWaferCount: z.number().int(),
        maxCapacity: z.number().int()
    })
});
export const ExportContainerHistorySchema = z.object({
    exportTimestamp: dateSchema,
    container: z.object({
        containerId: integerIdSchema,
        containerName: z.string().min(1),
    }),
    summaryStats: ContainerSummaryStatsSchema.shape.data,
    events: z.array(ContainerHistoryEventSchema),
    totalEvents: z.number().int(),
});
// ─── Physical Location Point (unified UWB + external provider) ────────────────
export const PhysicalLocationPointSchema = z.object({
    timestamp: dateSchema,
    source: z.enum(['uwb', 'external']),
    tagId: z.string().nullable(),
    x: z.number().nullable(),
    y: z.number().nullable(),
    z: z.number().nullable(),
    layerId: z.number().nullable(),
    zoneId: z.string().nullable(),
    zoneName: z.string().nullable(),
    floorId: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    floor: z.string().nullable(),
    room: z.string().nullable(),
    area: z.string().nullable(),
    provider: z.string().nullable()
});
export const ContainerPhysicalLocationHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        containerId: integerIdSchema,
        containerName: z.string(),
        points: z.array(PhysicalLocationPointSchema),
        totalCount: z.number().int(),
        hasUwbData: z.boolean(),
        hasExternalData: z.boolean()
    })
});
// Wafer physical location point — extends with container context
export const WaferLocationPointSchema = PhysicalLocationPointSchema.extend({
    containerId: integerIdSchema,
    containerName: z.string(),
    slotNumber: z.number().nullable()
});
export const WaferPhysicalLocationHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        waferId: integerIdSchema,
        waferName: z.string(),
        points: z.array(WaferLocationPointSchema),
        totalCount: z.number().int()
    })
});
export const WaferHistoryEventSchema = z.object({
    id: z.string().min(1),
    eventType: waferHistoryEventSchema,
    timestamp: dateSchema,
    description: z.string(),
    details: z.object({
        transferType: z.string().optional(),
        transferSource: z.string().optional(),
        notes: z.string().optional().nullable(),
        type: waferPatternTypeSchema.optional(),
        size: waferSizeSchema.optional(),
        material: waferMaterialTypeSchema.optional(),
        ownerFirstName: firstNameSchema.optional(),
        ownerLastName: lastNameSchema.optional(),
        receivedDate: dateSchema.optional(),
        customer: z.string().optional(),
        fingerprint: waferFingerprintSchema.optional(),
        hasImage: z.boolean().optional()
    }),
    performedBy: UserSummarySchema.optional(),
    containerInfo: z.object({
        fromContainer: z.object({
            id: integerIdSchema,
            name: z.string(),
            slot: containerSlotSchema.optional()
        }).optional(),
        toContainer: z.object({
            id: integerIdSchema,
            name: z.string(),
            slot: containerSlotSchema.optional()
        }).optional()
    }).optional(),
    locationInfo: z.object({
        previousLocation: z.string().optional(),
        newLocation: z.string().optional(),
        beacon: z.string().optional()
    }).optional()
});
export const WaferHistoryRequestSchema = z.object({
    startDate: dateSchema.optional(),
    endDate: dateSchema.optional(),
    eventTypes: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    sortBy: z.enum(['timestamp', 'eventType']).optional(),
    sortOrder: z.enum(['ASC', 'DESC']).optional()
});
export const WaferHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        waferId: integerIdSchema, //id of the wafer table
        waferName: z.string(),
        layerName: z.string(),
        events: z.array(WaferHistoryEventSchema),
        totalCount: z.number().int(),
        page: z.number().int(),
        pageSize: z.number().int(),
        hasMore: z.boolean()
    })
});
export const WaferSummaryStatsSchema = BaseResponseSchema.extend({
    data: z.object({
        totalEvents: z.number().int(),
        transfers: z.number().int(),
        statusChanges: z.number().int(),
        locationChanges: z.number().int(),
        fingerprintUpdates: z.number().int(),
        currentStatus: waferStatusSchema,
        currentContainer: z.string().optional(),
        currentSlot: z.number().optional()
    })
});
export { WaferSummarySchema, WaferSlotInfoSchema, ContainerSlotMapSchema, ContainerSlotsResponseSchema, } from './container-slot-map.schemas.js';
// Wafer Transfer Schema
export const WaferTransferResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        waferId: integerIdSchema,
        sourceContainerId: integerIdSchema,
        sourceSlotNumber: z.number().int().positive().optional(),
        targetContainerId: integerIdSchema,
        targetSlotNumber: z.number().int().positive(),
        comments: z.string().optional(),
        transferredAt: dateSchema,
        transferredBy: z.object({
            id: integerIdSchema,
            email: emailSchema.optional(),
            firstName: firstNameSchema.optional(),
            lastName: lastNameSchema.optional()
        }),
        message: z.string()
    })
});
// Wafer CRUD Response Schemas
export const CreateWaferResponseSchema = BaseResponseSchema.extend({
    data: WaferResponseSchema
});
export const GetWaferByIdResponseSchema = BaseResponseSchema.extend({
    data: WaferResponseSchema
});
export const UpdateWaferResponseSchema = BaseResponseSchema.extend({
    data: WaferResponseSchema
});
export const UpdateWaferFingerprintResponseSchema = BaseResponseSchema.extend({
    data: WaferResponseSchema
});
export const DeleteWaferFilesResponseSchema = BaseResponseSchema.extend({
    data: EntityFilesDeleteResponseDataSchema,
});
export const WaferLocationHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        waferId: integerIdSchema,
        waferName: z.string(),
        containerStays: z.array(WaferContainerStaySchema)
    })
});
export const ExportWaferHistoryResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        exportTimestamp: dateSchema,
        wafer: z.object({
            waferId: integerIdSchema,
            waferName: z.string(),
            layerName: z.string()
        }),
        history: z.array(WaferHistoryEventSchema)
    })
});
// Usage Tracking Response Schemas
export const CurrentUsageResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        usage: z.object({
            users: z.number().int(),
            containers: z.number().int(),
            wafers: z.number().int(),
            storageUsedBytes: z.coerce.number().int(),
            apiCallsThisMonth: z.number().int(),
            lastUpdated: dateSchema
        }),
        tenantId: uuidSchema
    })
});
export const UsageLimitsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        isWithinLimits: z.boolean(),
        limits: z.object({
            maxUsers: z.number().int(),
            maxContainers: z.number().int(),
            maxWafers: z.number().int(),
            maxStorageBytes: z.coerce.number().int(),
            maxApiCallsPerMonth: z.number().int()
        }),
        currentUsage: z.object({
            users: z.number().int(),
            containers: z.number().int(),
            wafers: z.number().int(),
            storageUsedBytes: z.coerce.number().int(),
            apiCallsThisMonth: z.number().int()
        }),
        tenantId: uuidSchema,
        subscriptionId: uuidSchema.optional()
    })
});
export const UsageFeaturesResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        featureGates: z.array(z.object({
            feature: z.string(),
            enabled: z.boolean(),
            reason: z.string().optional()
        })),
        summary: z.object({
            enabled: z.array(z.string()),
            disabled: z.array(z.object({
                feature: z.string(),
                reason: z.string().optional()
            })),
            totalFeatures: z.number().int(),
            enabledCount: z.number().int()
        }),
        tenantId: uuidSchema,
        subscriptionId: uuidSchema.optional()
    })
});
export const UsageDashboardResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        usage: z.object({
            users: z.number().int(),
            containers: z.number().int(),
            wafers: z.number().int(),
            storageUsedBytes: z.coerce.number().int(),
            apiCallsThisMonth: z.number().int(),
            lastUpdated: dateSchema
        }),
        limits: z.object({
            isWithinLimits: z.boolean(),
            limits: z.object({
                maxUsers: z.number().int(),
                maxContainers: z.number().int(),
                maxWafers: z.number().int(),
                maxStorageBytes: z.coerce.number().int(),
                maxApiCallsPerMonth: z.number().int()
            }),
            currentUsage: z.object({
                users: z.number().int(),
                containers: z.number().int(),
                wafers: z.number().int(),
                storageUsedBytes: z.coerce.number().int(),
                apiCallsThisMonth: z.number().int()
            })
        }),
        features: z.object({
            gates: z.array(z.object({
                feature: z.string(),
                enabled: z.boolean(),
                reason: z.string().optional()
            })),
            enabled: z.array(z.string()),
            disabled: z.array(z.object({
                feature: z.string(),
                reason: z.string().optional()
            }))
        }),
        metrics: z.object({
            usagePercentages: z.object({
                users: z.number(),
                containers: z.number(),
                wafers: z.number(),
                storage: z.coerce.number(),
                apiCalls: z.number()
            }),
            healthScore: z.number().int(),
            lastUpdated: dateSchema
        }),
        tenantId: uuidSchema,
        subscriptionId: uuidSchema.optional()
    })
});
// Webhook Response Schemas
export const StripeWebhookResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        eventId: z.string(),
        eventType: z.string(),
        message: z.string()
    })
});
export const StripeWebhookErrorResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        message: z.string(),
        details: z.any().optional()
    })
});
export const SupportedEventsResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        supportedEvents: z.array(z.string()),
        count: z.number().int(),
        eventCategories: z.object({
            subscription: z.array(z.string()),
            invoice: z.array(z.string()),
            payment: z.array(z.string()),
            customer: z.array(z.string()),
            setupIntent: z.array(z.string())
        })
    })
});
export const TestWebhookResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        eventType: z.string(),
        eventId: z.string(),
        isSupported: z.boolean(),
        message: z.string()
    })
});
export const TestWebhookErrorResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        message: z.string()
    })
});
// Health Check Response Schemas
export const HealthCheckResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        status: healthStatusSchema,
        timestamp: z.string(),
        uptime: z.number(),
        service: z.string(),
        version: z.string(),
        checks: z.object({
            database: healthCheckStatusSchema,
            redis: healthCheckStatusSchema,
            memory: z.object({
                rss: z.number(),
                heapTotal: z.number(),
                heapUsed: z.number(),
                external: z.number()
            })
        })
    })
});
export const ReadinessResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        status: readinessStatusSchema,
        timestamp: z.string()
    })
});
export const VersionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        version: z.string(),
        buildSha: z.string().optional(),
        service: z.string(),
        environment: z.string().optional(),
    })
});
export const LivenessResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        status: livenessStatusSchema,
        timestamp: z.string(),
        uptime: z.number()
    })
});
export const HealthErrorResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        status: healthStatusSchema,
        timestamp: z.string(),
        error: z.string()
    })
});
export const ReadinessErrorResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        status: readinessStatusSchema,
        error: z.string()
    })
});
// ML Service Response Schemas
export const MLAnalysisResultSchema = z.object({
    id: z.string(),
    success: z.boolean(),
    waferSerial: z.string().optional(),
    overallConfidence: z.number(),
    ocrResult: z.object({
        success: z.boolean(),
        confidence: z.number(),
        extractedText: z.string().optional(),
        error: z.string().optional()
    }).optional(),
    cnnResult: z.object({
        success: z.boolean(),
        confidence: z.number(),
        fingerprint: z.string().optional(),
        matched: z.boolean().optional(),
        error: z.string().optional()
    }).optional(),
    llmResult: z.object({
        success: z.boolean(),
        confidence: z.number(),
        validationResult: z.string().optional(),
        error: z.string().optional()
    }).optional(),
    processingTime: z.number(),
    timestamp: z.string()
});
export const MLSingleAnalysisResponseSchema = BaseResponseSchema.extend({
    data: MLAnalysisResultSchema
});
export const MLBatchAnalysisResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        results: z.array(MLAnalysisResultSchema),
        total: z.number().int(),
        successful: z.number().int(),
        failed: z.number().int(),
        processingTime: z.number(),
        batchId: z.string()
    })
});
export const MLOCRExtractionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        success: z.boolean(),
        extractedText: z.string(),
        confidence: z.number(),
        words: z.array(z.object({
            text: z.string(),
            confidence: z.number(),
            bbox: z.object({
                x0: z.number(),
                y0: z.number(),
                x1: z.number(),
                y1: z.number()
            })
        })),
        processingTime: z.number(),
        imageQuality: imageQualitySchema,
        validation: z.any().optional()
    })
});
export const MLFingerprintExtractionResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        success: z.boolean(),
        id: z.string(),
        features: z.array(z.number()),
        metadata: z.object({
            extractedAt: z.date(),
            imageQuality: imageQualitySchema,
            processingTime: z.number(),
            algorithm: z.string(),
            version: z.string()
        })
    })
});
export const MLSystemStatusResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        overall: z.object({
            healthy: z.boolean(),
            analysisCount: z.number().int(),
            uptime: z.number()
        }),
        components: z.object({
            ocr: z.object({
                status: mlComponentStatusSchema,
                initialized: z.boolean()
            }),
            cnn: z.object({
                status: mlComponentStatusSchema,
                initialized: z.boolean(),
                cachedFingerprints: z.number().int(),
                memoryUsage: z.number()
            }),
            llm: z.object({
                status: mlComponentStatusSchema,
                provider: z.string().optional(),
                model: z.string().optional()
            }),
            preprocessing: z.object({
                status: z.string(),
                processedCount: z.number().int()
            })
        }),
        configuration: z.object({
            ocr: z.object({
                enabled: z.boolean(),
                confidenceThreshold: z.number(),
                languages: z.array(z.string())
            }),
            cnn: z.object({
                enabled: z.boolean(),
                modelPath: z.string(),
                threshold: z.number()
            }),
            llm: z.object({
                enabled: z.boolean(),
                provider: z.string(),
                model: z.string(),
                temperature: z.number(),
                maxTokens: z.number()
            }),
            validation: z.object({
                weightings: z.object({
                    ocr: z.number(),
                    cnn: z.number(),
                    llm: z.number()
                }),
                overallThreshold: z.number()
            })
        })
    })
});
export const MLConfigUpdateResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        config: z.object({
            ocr: z.object({
                enabled: z.boolean(),
                confidenceThreshold: z.number(),
                languages: z.array(z.string())
            }),
            cnn: z.object({
                enabled: z.boolean(),
                modelPath: z.string(),
                threshold: z.number()
            }),
            llm: z.object({
                enabled: z.boolean(),
                provider: z.string(),
                model: z.string(),
                temperature: z.number(),
                maxTokens: z.number()
            }),
            validation: z.object({
                weightings: z.object({
                    ocr: z.number(),
                    cnn: z.number(),
                    llm: z.number()
                }),
                overallThreshold: z.number()
            })
        })
    })
});
export const MLCleanupResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        cleanedFiles: z.number().int(),
        olderThanHours: z.number()
    })
});
export const ErrorResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        error: z.string(),
        details: z.string().optional()
    })
});
// Operations Schemas (for frontend forms)
export const CreateOperationsWaferSchema = z.object({
    waferId: waferFingerprintSchema,
    lotNumber: z.string().min(1, "Lot number is required"),
    waferNumber: z.number().int().positive("Wafer number must be positive"),
    diameter: z.string().min(1, "Diameter is required"),
    thickness: waferThicknessTypeSchema,
    material: waferMaterialTypeSchema,
    dopingType: z.string().optional(),
    resistivity: z.number().optional(),
    orientation: z.string().optional(),
    supplier: z.string().optional(),
    location: z.string().optional(),
    containerId: integerIdSchema,
    containerSlot: z.number().int().optional(),
    notes: z.string().optional(),
});
export const CreateOperationsContainerSchema = z.object({
    containerNumber: z.string().min(1, "Container number is required"),
    type: containerTypeSchema,
    location: z.string().optional(),
    capacity: z.number().int().positive("Capacity must be positive"),
    assignedTo: z.string().optional(),
    notes: z.string().optional(),
});
// API Response and Error Schemas
export const ApiResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    data: z.unknown().optional(),
    error: z.string().optional(),
    errors: z.array(z.object({
        field: z.string(),
        message: z.string()
    })).optional()
});
export const ApiErrorResponseSchema = z.object({
    success: z.literal(false),
    message: z.string(),
    error: z.string().optional(),
    errors: z.array(z.object({
        field: z.string(),
        message: z.string()
    })).optional(),
    details: z.array(z.object({
        field: z.string(),
        message: z.string(),
        code: z.string().optional(),
        value: z.any().optional()
    })).optional()
});
export const ValidationErrorDetailSchema = z.object({
    field: z.string(),
    message: z.string(),
    code: z.string().optional(),
    value: z.any().optional()
});
// Note: Old SubscriptionPlanTemplate schemas removed - now using unified Subscription model
// Parameter Schemas
export const UserIdParamSchema = z.object({
    userId: integerIdSchema
});
// Param validation schemas for EFEM routes
export const efemIdParamSchema = z.object({
    id: integerIdSchema
});
export const LoadPortIdParamSchema = z.object({
    loadPortId: z.coerce.number().int().positive()
});
export const EfemGatewayLoadPortParamSchema = EfemGatewaySerialParamSchema.merge(LoadPortIdParamSchema);
export const CollectWaferImagesRequestSchema = z.object({
    loadPortId: z.number().int().positive(),
    slotNumbers: z.array(z.number().int().positive()).min(1),
});
export const LoadPortIdsSchema = z.object({
    loadPortIds: z.array(z.number().int().positive()).min(1)
});
export const LoadPortsLoadUnloadResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        loadPortId: z.number().int().positive(),
        success: z.boolean(),
        error: z.string().optional(),
    })
});
export const BulkLoadPortsLoadUnloadResponseSchema = BaseResponseSchema.extend({
    data: z.array(LoadPortsLoadUnloadResponseSchema.shape.data)
});
// Registration Schemas
export const RegisterRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    role: allUserRoleSchema
});
export const SignUpRequestSchema = RegisterRequestSchema.extend({
    companyName: z.string().min(1, 'Company name is required'),
    confirmPassword: passwordSchema,
    acceptTerms: z.boolean().refine(val => val === true, {
        message: 'You must accept the terms and conditions',
    }),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});
export const ProviderRegisterRequestSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    role: allUserRoleSchema
});
export const ProviderRegisterResponseSchema = BaseResponseSchema.extend({
    data: z.object({
        user: z.object({
            id: integerIdSchema,
            email: emailSchema,
            firstName: firstNameSchema,
            lastName: lastNameSchema,
            role: allUserRoleSchema,
            isActive: z.boolean()
        })
    })
});
// Query validation schemas for routes
export const CustomerInvoicesQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional(),
    //status: z.enum(['draft', 'open', 'paid', 'uncollectible', 'void']).optional() // TODO: need to get this enum from Stripe library directly
    status: z.enum(['active', 'canceled', 'incomplete', 'incomplete_expired', 'past_due', 'trialing', 'unpaid']).optional()
});
export const InvoiceIdParamSchema = z.object({
    invoiceId: uuidSchema,
});
export const CreateInvoiceSchema = z.object({
    customerId: uuidSchema,
    amount: z.number().min(0.50),
    currency: z.string().length(3),
    description: z.string().max(500).optional(),
    metadata: z.record(z.any()).optional()
});
//# sourceMappingURL=audit-history.schemas.js.map